import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { SubscriptionChargeStatus } from "../../../app/constants";
import {
  updateSubscription,
  getParkitSubscriptions,
  manualChargeParkitSubscription,
  pauseChargeSubscription,
  recreateSubscription,
} from "../../../api/parkitSubscriptionAPI";

export const subscriptionSlice = createSlice({
  name: "parkitFailedSubscription",
  initialState: {
    failedSubscriptions: [],
    failedSubscriptionPagination: {},
    failedSubscriptionPage: 0,
    failedSubscriptionRowsPerPage: 50,
    sort: "desc",
    sortBy: "nextBillingDate",
    selectedFailedSubscription: null,
    selectedFilter: {
      chargeStatus: [
        SubscriptionChargeStatus.FAILED,
        SubscriptionChargeStatus.RETRYING,
      ],
    },
    confirmRetryPaymentModalOpen: false,
    confirmPauseChargeModalOpen: false,
    recreateSubscriptionModalOpen: false,
    filterModalOpen: false,
    successMessage: "",
    errorMessage: "",
  },
  reducers: {
    openConfirmRetryPaymentModal: (state) => {
      state.confirmRetryPaymentModalOpen = true;
    },
    closeConfirmRetryPaymentModal: (state) => {
      state.confirmRetryPaymentModalOpen = false;
    },
    setSelectedFailedSubscription: (state, action) => {
      state.selectedFailedSubscription = action.payload;
    },
    setFailedSubscriptionPage: (state, action) => {
      state.failedSubscriptionPage = action.payload;
    },
    setFailedSubscriptionRowsPerPage: (state, action) => {
      state.failedSubscriptionRowsPerPage = action.payload;
    },
    setFailedSubscriptionPagination: (state, action) => {
      state.failedSubscriptionPagination = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setFailedSubscriptions: (state, action) => {
      state.failedSubscriptions = action.payload;
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    openConfirmPauseChargeModal: (state) => {
      state.confirmPauseChargeModalOpen = true;
    },
    closeConfirmPauseChargeModal: (state) => {
      state.confirmPauseChargeModalOpen = false;
    },
    openRecreateSubscriptionModal: (state) => {
      state.recreateSubscriptionModalOpen = true;
    },
    closeRecreateSubscriptionModal: (state) => {
      state.recreateSubscriptionModalOpen = false;
    },
    showFilterModal: (state, action) => {
      state.filterModalOpen = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setSort,
  setSortBy,
  setSelectedFilter,
  showFilterModal,
  openRecreateSubscriptionModal,
  closeRecreateSubscriptionModal,
  openConfirmRetryPaymentModal,
  closeConfirmRetryPaymentModal,
  setSelectedFailedSubscription,
  setFailedSubscriptionPage,
  setFailedSubscriptionRowsPerPage,
  setFailedSubscriptionPagination,
  setFailedSubscriptions,
  openConfirmPauseChargeModal,
  closeConfirmPauseChargeModal,
  setSuccessMessage,
  setErrorMessage,
} = subscriptionSlice.actions;

export const selectState = (state) => state.parkitFailedSubscription;

export const handleGetFailedSubscriptions =
  () => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      const { selectedFilter } = state.parkitFailedSubscription;
      const chargeStatues = selectedFilter.chargeStatus.join(",");
      const response = await getParkitSubscriptions(
        state.parkitFailedSubscription.failedSubscriptionPage,
        state.parkitFailedSubscription.failedSubscriptionRowsPerPage,
        state.parkitFailedSubscription.sort,
        state.parkitFailedSubscription.sortBy,
        chargeStatues
      );
      const { content, ...pagination } = response.data;

      dispatch(setFailedSubscriptionPagination(pagination));
      dispatch(setFailedSubscriptions(content));
    } catch (error) {
      dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
      dispatch(setFailedSubscriptions([]));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleRetryPayment = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();
  try {
    await manualChargeParkitSubscription(
      state.parkitFailedSubscription.selectedFailedSubscription.id
    );
    dispatch(handleGetFailedSubscriptions());
    dispatch(closeConfirmRetryPaymentModal());
    dispatch(
      setSuccessMessage("Subscription payment retry requested successfully")
    );
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handlePauseCharge = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();
  try {
    await pauseChargeSubscription(
      state.parkitFailedSubscription.selectedFailedSubscription.id,
      state.parkitFailedSubscription.selectedFailedSubscription.walletId
    );
    dispatch(handleGetFailedSubscriptions());
    dispatch(closeConfirmPauseChargeModal());
    dispatch(
      setSuccessMessage("Subscription pause charge requested successfully")
    );
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleRecreateSubcription = (payload) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await recreateSubscription(payload);
    dispatch(handleGetFailedSubscriptions());
    dispatch(closeRecreateSubscriptionModal());
    dispatch(setSuccessMessage("Recreate subscription requested successfully"));
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleEditSubscription = (payload) => async (dispatch) => {
  dispatch(showLoading());
  try {
    await updateSubscription(payload.id, payload);
    dispatch(handleGetFailedSubscriptions());
    dispatch(setSuccessMessage("Successfully update subscription"));
  } catch (error) {
    dispatch(setErrorMessage(error.response?.data?.message ?? error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export default subscriptionSlice.reducer;
